import { COLUMN_PRESETS, STANDARD_COLUMN_WIDTHS } from '../column-widths'
import { FieldTypeEnum } from '../custom-field.types'
import { Deployment } from '../deployments'
import { ModuleColumnDefinition } from '../module'
import { ModuleTypeEnum } from '../module-type'

/**
 * Standard columns for the project deployments table. Deployments are
 * project-scoped, so unlike other modules there are no custom fields — the
 * table renders exactly these columns. `environmentId`/`status` resolve their
 * options via relationshipData supplied by the page.
 */
export const DEPLOYMENT_STANDARD_COLUMNS: ModuleColumnDefinition[] = [
  {
    id: 'name',
    name: 'name' as keyof Deployment,
    label: 'Deployment',
    description: 'Deployment name',
    isVisible: true,
    isRequired: true,
    isSystem: true,
    isFixed: true,
    order: 0,
    fieldType: FieldTypeEnum.enum.STRING,
    ...COLUMN_PRESETS.NAME,
  },
  {
    id: 'version',
    name: 'version' as keyof Deployment,
    label: 'Version',
    description: 'Release version (e.g. v1.4.0)',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 1,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'environmentId',
    name: 'environmentId' as keyof Deployment,
    label: 'Environment',
    description: 'Environment this deployment targets',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 2,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    ...COLUMN_PRESETS.STATUS,
  },
  {
    id: 'status',
    name: 'status' as keyof Deployment,
    label: 'Status',
    description: 'Deployment lifecycle status',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 3,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    ...COLUMN_PRESETS.STATUS,
  },
  {
    id: 'itemCount',
    name: 'itemCount' as keyof Deployment,
    label: 'Items',
    description: 'Tasks and tickets in scope',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 4,
    fieldType: FieldTypeEnum.enum.NUMBER,
    ...COLUMN_PRESETS.COUNT,
  },
  {
    id: 'scheduledFor',
    name: 'scheduledFor' as keyof Deployment,
    label: 'Scheduled',
    description: 'Planned release date',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 5,
    fieldType: FieldTypeEnum.enum.DATE,
    ...COLUMN_PRESETS.DUE_DATE,
  },
  {
    id: 'deployedAt',
    name: 'deployedAt' as keyof Deployment,
    label: 'Deployed',
    description: 'When the deployment completed',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 6,
    fieldType: FieldTypeEnum.enum.DATETIME,
    ...STANDARD_COLUMN_WIDTHS.MEDIUM,
  },
  {
    id: 'repository',
    name: 'repository' as keyof Deployment,
    label: 'Repository',
    description: 'Linked GitHub repository and target ref',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 7,
    fieldType: FieldTypeEnum.enum.STRING,
    ...STANDARD_COLUMN_WIDTHS.WIDE,
  },
  {
    id: 'showInPortal',
    name: 'showInPortal' as keyof Deployment,
    label: 'Client Portal',
    description: 'Visible to client portal users',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 8,
    fieldType: FieldTypeEnum.enum.BOOLEAN,
    ...STANDARD_COLUMN_WIDTHS.NARROW,
  },
  {
    id: 'createdAt',
    name: 'createdAt' as keyof Deployment,
    label: 'Created Date',
    description: 'Date the deployment was created',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 99,
    fieldType: FieldTypeEnum.enum.DATETIME,
    ...COLUMN_PRESETS.CREATED_DATE,
  },
  {
    id: 'updatedAt',
    name: 'updatedAt' as keyof Deployment,
    label: 'Last Updated',
    description: 'Date the deployment was last updated',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 100,
    fieldType: FieldTypeEnum.enum.DATETIME,
    ...COLUMN_PRESETS.UPDATED_DATE,
  },
]

export function getDeploymentColumns(): ModuleColumnDefinition[] {
  return DEPLOYMENT_STANDARD_COLUMNS
}

export function getDeploymentModuleType(): ModuleTypeEnum {
  return ModuleTypeEnum.DEPLOYMENT
}
